import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, Badge, Button, Divider, Form, Input, List, Spin, Tag } from "antd";
import { useCallback, useRef } from "react";
import { ESupportTicketQuestionType } from "@devowl-wp/api-real-commerce";
import { IMPREZA_BUTTON_PROPS } from "../../components/provider.js";
import { mapSupportLanguageToKnowledgeBaseDataLocale, useKnowledgeBaseArticles } from "../../hooks/useKnowledgeBaseArticles.js";
import { useSupportFormProductType } from "../../hooks/useSupportFormProductType.js";
import { __, _i } from "../../utils/i18n.js";
const SupportFormConcernField = (param)=>{
    let { concernSubmitted, onArticlesLoaded, onArticlesNotSuitable } = param;
    const { loading, dataLocale, setDataLocale, knowledgeBaseHits, handleSearchKnowledgeBaseHits } = useKnowledgeBaseArticles();
    const productTypes = useSupportFormProductType();
    const memoizeLastProduct = useRef(null);
    const handleSearch = useCallback(async (term, product, debounce, force)=>{
        if (concernSubmitted !== "initial" || force) {
            var _productTypes_product;
            const knowledgeBaseTopics = (_productTypes_product = productTypes[product]) == null ? void 0 : _productTypes_product.knowledgeBaseTopics;
            return await handleSearchKnowledgeBaseHits(term, knowledgeBaseTopics ? knowledgeBaseTopics.join(",") : "", debounce);
        } else {
            return [];
        }
    }, [
        handleSearchKnowledgeBaseHits,
        productTypes,
        concernSubmitted
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Form.Item, {
                shouldUpdate: (prevValues, nextValues)=>prevValues.questionType !== nextValues.questionType || prevValues.product !== nextValues.product || prevValues.languageOfSupport !== nextValues.languageOfSupport,
                noStyle: true,
                children: (param)=>{
                    let { getFieldValue, validateFields } = param;
                    // Reload knowledge base articles when changing subject
                    const questionType = getFieldValue("questionType");
                    const isCustomOfferRequest = questionType === ESupportTicketQuestionType.RequestCustomOffer;
                    const product = getFieldValue("product");
                    const languageOfSupport = getFieldValue("languageOfSupport");
                    const newDataLocale = mapSupportLanguageToKnowledgeBaseDataLocale(languageOfSupport);
                    if (dataLocale !== newDataLocale) {
                        // `setDataLocale` does reload this form so do no search atm
                        setTimeout(()=>setDataLocale(newDataLocale), 0);
                    } else {
                        if (isCustomOfferRequest) {
                            memoizeLastProduct.current = null;
                            setTimeout(()=>handleSearch("", undefined), 0);
                        } else if (memoizeLastProduct.current !== product) {
                            memoizeLastProduct.current = product;
                            setTimeout(()=>handleSearch(getFieldValue("concern"), product), 0);
                        }
                    }
                    return isCustomOfferRequest || !questionType ? null : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Describe your question in short words"),
                                required: true,
                                extra: _i(__("Please try to describe {{strong}}your request in just 2-5 words{{/strong}}. This way we can assign your ticket to the right expert faster and recommend you suitable articles from our knowledge base."), {
                                    strong: /*#__PURE__*/ _jsx("strong", {})
                                }),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        noStyle: true,
                                        name: "concern",
                                        rules: [
                                            {
                                                required: true,
                                                message: __("Please insert your question!")
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            onChangeCapture: (e)=>{
                                                !isCustomOfferRequest && handleSearch(e.target.value, getFieldValue("product"), 3000);
                                            },
                                            onBlur: (e)=>{
                                                !isCustomOfferRequest && handleSearch(e.target.value, getFieldValue("product"));
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        noStyle: true,
                                        shouldUpdate: (prevValues, curValues)=>prevValues.concern !== curValues.concern,
                                        children: (param)=>{
                                            let { getFieldValue } = param;
                                            var _getFieldValue;
                                            return concernSubmitted === "initial" && ((_getFieldValue = getFieldValue("concern")) == null ? void 0 : _getFieldValue.split(" ").length) > 15 ? /*#__PURE__*/ _jsx(Alert, {
                                                style: {
                                                    margin: "15px 0"
                                                },
                                                message: /*#__PURE__*/ _jsx(_Fragment, {
                                                    children: __("Please describe the problem in brief, like in an email subject! You can describe your problem in more detail later in the message field.")
                                                }),
                                                type: "info"
                                            }) : null;
                                        }
                                    })
                                ]
                            }),
                            concernSubmitted === "initial" && /*#__PURE__*/ _jsx(Form.Item, {
                                style: {
                                    textAlign: "center"
                                },
                                wrapperCol: {
                                    span: 24
                                },
                                children: /*#__PURE__*/ _jsx(Button, {
                                    type: "primary",
                                    size: "large",
                                    ...IMPREZA_BUTTON_PROPS,
                                    htmlType: "button",
                                    loading: loading,
                                    onClick: async ()=>{
                                        try {
                                            await validateFields();
                                            const articles = await handleSearch(getFieldValue("concern"), product, 0, true);
                                            if (articles.length > 0) {
                                                onArticlesLoaded();
                                            } else {
                                                onArticlesNotSuitable();
                                            }
                                        } catch (e) {
                                        // Silence is golden.
                                        }
                                    },
                                    children: __("Continue")
                                })
                            })
                        ]
                    });
                }
            }),
            concernSubmitted !== "initial" && (knowledgeBaseHits == null ? void 0 : knowledgeBaseHits.length) > 0 && /*#__PURE__*/ _jsxs(Spin, {
                spinning: loading,
                delay: 1500,
                children: [
                    /*#__PURE__*/ _jsx(Divider, {
                        orientation: "left",
                        children: __("Do these articles answer your question?")
                    }),
                    /*#__PURE__*/ _jsx(List, {
                        itemLayout: "horizontal",
                        dataSource: knowledgeBaseHits,
                        renderItem: (param)=>{
                            let { introduction, title, toc, permalink, subtitle } = param;
                            return /*#__PURE__*/ _jsx(List.Item, {
                                onClick: ()=>window.open(permalink, "_blank"),
                                style: {
                                    cursor: "pointer"
                                },
                                children: /*#__PURE__*/ _jsx(List.Item.Meta, {
                                    title: /*#__PURE__*/ _jsx("strong", {
                                        dangerouslySetInnerHTML: {
                                            __html: title
                                        }
                                    }),
                                    description: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                dangerouslySetInnerHTML: {
                                                    __html: introduction
                                                }
                                            }),
                                            toc.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                                style: {
                                                    lineHeight: 2
                                                },
                                                children: [
                                                    __("Table of contents:"),
                                                    "\xa0",
                                                    toc.map((t)=>/*#__PURE__*/ _jsxs("span", {
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Badge, {
                                                                    dot: t === subtitle,
                                                                    children: /*#__PURE__*/ _jsx(Tag, {
                                                                        style: {
                                                                            margin: 0
                                                                        },
                                                                        children: t
                                                                    })
                                                                }),
                                                                "\xa0\xa0"
                                                            ]
                                                        }, t))
                                                ]
                                            })
                                        ]
                                    })
                                })
                            });
                        }
                    }),
                    concernSubmitted === "loaded" && /*#__PURE__*/ _jsx(Form.Item, {
                        style: {
                            textAlign: "center"
                        },
                        wrapperCol: {
                            span: 24
                        },
                        children: /*#__PURE__*/ _jsx(Button, {
                            type: "primary",
                            size: "large",
                            ...IMPREZA_BUTTON_PROPS,
                            htmlType: "button",
                            loading: loading,
                            onClick: onArticlesNotSuitable,
                            children: __("Continue opening support ticket")
                        })
                    })
                ]
            })
        ]
    });
};
export { SupportFormConcernField };
